import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Flex,
  useColorModeValue,
  Progress,
  VStack,
  HStack,
  Badge,
  Button,
  SimpleGrid,
  Divider,
  Spinner
} from '@chakra-ui/react';
import {
  MdAssignment,
  MdCheckCircle,
  MdPending,
  MdCancel,
  MdGroup,
  MdTrendingUp,
  MdDescription,
  MdGavel,
  MdAccessTime
} from 'react-icons/md';
import { FaBalanceScale } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';

const GestorJuridicoDashboard = () => {
  const [stats, setStats] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const highlightBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, teamResponse] = await Promise.all([
        api.get('/juridico/stats'),
        api.get('/juridico/team/overview')
      ]);

      setStats(statsResponse.stats || statsResponse.data?.stats || {});
      setTeamStats(teamResponse.data || teamResponse || {});
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtext, growth }) => (
    <Card bg={cardBg} shadow="sm">
      <CardBody>
        <Flex justify="space-between" align="flex-start">
          <Box flex={1}>
            <Text fontSize="sm" color={mutedColor} mb={1}>
              {title}
            </Text>
            <Stat>
              <StatNumber fontSize="2xl" color={textColor}>
                {value}
              </StatNumber>
              {subtext && (
                <StatHelpText>
                  {growth && <StatArrow type={growth > 0 ? 'increase' : 'decrease'} />}
                  {subtext}
                </StatHelpText>
              )}
            </Stat>
          </Box>
          <Icon as={icon} w={8} h={8} color={color} />
        </Flex>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  const teamTotals = teamStats?.data?.teamTotals || {};
  const teamMembers = teamStats?.data?.teamMembers || [];

  return (
    <Box>
      <Heading size="lg" mb={6} color={textColor}>
        Dashboard - Gestão Jurídica
      </Heading>

      {/* Main Statistics */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
        <GridItem>
          <StatCard
            title="Total de Análises"
            value={teamTotals.totalAssignments || 0}
            icon={MdAssignment}
            color="purple.500"
          />
        </GridItem>
        <GridItem>
          <StatCard
            title="Análises Concluídas"
            value={teamTotals.completedAssignments || 0}
            icon={MdCheckCircle}
            color="green.500"
            subtext={`${teamTotals.teamCompletionRate || 0}% do total`}
          />
        </GridItem>
        <GridItem>
          <StatCard
            title="Em Análise"
            value={teamTotals.inAnalysisAssignments || 0}
            icon={MdAccessTime}
            color="blue.500"
          />
        </GridItem>
        <GridItem>
          <StatCard
            title="Pendentes"
            value={teamTotals.pendingAssignments || 0}
            icon={MdPending}
            color="orange.500"
          />
        </GridItem>
      </Grid>

      {/* Team Performance Section */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} mb={8}>
        {/* Team Overview */}
        <Card bg={cardBg} shadow="sm">
          <CardHeader>
            <Heading size="md">Desempenho da Equipe</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Team Summary */}
              <HStack spacing={4} p={4} bg={highlightBg} borderRadius="md">
                <Icon as={MdGroup} w={6} h={6} color="purple.500" />
                <Box flex={1}>
                  <Text fontWeight="semibold">{teamMembers.length} membros ativos</Text>
                  <Text fontSize="sm" color={mutedColor}>
                    Taxa de aprovação: {teamTotals.teamApprovalRate || 0}%
                  </Text>
                </Box>
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="ghost"
                  onClick={() => navigate('/gestor-juridico/equipe')}
                >
                  Ver Equipe
                </Button>
              </HStack>

              {/* Top Performers */}
              <Box>
                <Text fontWeight="semibold" mb={3}>Top Analistas</Text>
                <VStack align="stretch" spacing={2}>
                  {teamMembers
                    .sort((a, b) => b.stats.completionRate - a.stats.completionRate)
                    .slice(0, 3)
                    .map((member, index) => (
                      <HStack key={member.member._id} p={2} borderRadius="md" bg={highlightBg}>
                        <Badge colorScheme={index === 0 ? 'gold' : index === 1 ? 'gray' : 'orange'}>
                          {index + 1}º
                        </Badge>
                        <Text fontSize="sm" flex={1}>{member.member.name}</Text>
                        <VStack align="end" spacing={0}>
                          <Text fontSize="sm" fontWeight="semibold">{member.stats.completionRate}%</Text>
                          <Text fontSize="xs" color={mutedColor}>{member.stats.completed} concluídas</Text>
                        </VStack>
                      </HStack>
                    ))}
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Document Analysis Stats */}
        <Card bg={cardBg} shadow="sm">
          <CardHeader>
            <Heading size="md">Análise de Documentos</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Flex justify="space-between" mb={2}>
                  <Text fontSize="sm">Taxa de Aprovação</Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {teamTotals.teamApprovalRate || 0}%
                  </Text>
                </Flex>
                <Progress 
                  value={parseFloat(teamTotals.teamApprovalRate) || 0} 
                  colorScheme="green" 
                  borderRadius="full"
                />
              </Box>
              
              <Divider />
              
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontSize="xs" color={mutedColor}>Aprovados</Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.500">
                    {teamTotals.approvedDocuments || 0}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={mutedColor}>Reprovados</Text>
                  <Text fontSize="xl" fontWeight="bold" color="red.500">
                    {teamTotals.reprovedDocuments || 0}
                  </Text>
                </Box>
              </SimpleGrid>
              
              <Box>
                <Text fontSize="xs" color={mutedColor}>Total de Documentos</Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {teamTotals.totalDocuments || 0}
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
        <Card 
          bg={cardBg} 
          shadow="sm" 
          cursor="pointer"
          onClick={() => navigate('/gestor-juridico/equipe')}
          _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <CardBody>
            <HStack spacing={4}>
              <Icon as={MdGroup} w={8} h={8} color="green.500" />
              <Box>
                <Text fontWeight="semibold">Gerenciar Equipe</Text>
                <Text fontSize="sm" color={mutedColor}>
                  Ver desempenho individual
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card 
          bg={cardBg} 
          shadow="sm" 
          cursor="pointer"
          onClick={() => navigate('/gestor-juridico/precatorios-disponiveis')}
          _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <CardBody>
            <HStack spacing={4}>
              <Icon as={MdGavel} w={8} h={8} color="orange.500" />
              <Box>
                <Text fontWeight="semibold">Precatórios Disponíveis</Text>
                <Text fontSize="sm" color={mutedColor}>
                  Atribuir para análise
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card 
          bg={cardBg} 
          shadow="sm" 
          cursor="pointer"
          onClick={() => navigate('/gestor-juridico/meus-precatorios')}
          _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
          transition="all 0.2s"
        >
          <CardBody>
            <HStack spacing={4}>
              <Icon as={MdDescription} w={8} h={8} color="blue.500" />
              <Box>
                <Text fontWeight="semibold">Minhas Análises</Text>
                <Text fontSize="sm" color={mutedColor}>
                  Continuar análises
                </Text>
              </Box>
            </HStack>
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default GestorJuridicoDashboard;